import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";

import supabase from "../lib/supabase";
import { effectieveTenantRegel } from "../lib/tenantUserAccess";
import { traceAuthTransition } from "../lib/authTransitionTrace";

type AuthState = { session: Session | null; loading: boolean; user: User | null };
const AuthContext = createContext<AuthState | null>(null);

/** Eén sessiebron boven de router: routewissels starten geen nieuw sessieherstel. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let eventReceived = false;

    const loadSession = async () => {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      if (!isMounted || eventReceived) {
        traceAuthTransition("restore_ignored");
        return;
      }

      // Alleen lokaal sessieherstel. Servergeldigheid blijft in ProtectedRoute.
      setSession(currentSession);
      traceAuthTransition("session_restored", { authenticated: Boolean(currentSession) });
      setLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted) {
        return;
      }
      eventReceived = true;
      traceAuthTransition("auth_event", { event, authenticated: Boolean(nextSession) });
      setSession(nextSession);
      setLoading(false);
    });

    // Eerst luisteren: een nieuwer auth-event wint van deze oudere response.
    void loadSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return createElement(AuthContext.Provider, { value: {
    session,
    loading,
    user: session?.user ?? null,
  } }, children);
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) throw new Error("useAuth requires AuthProvider");
  return state;
}

/**
 * DE TENANT NAMENS WIE DEZE SESSIE WERKT.
 *
 * DIT WAS DE THUISTENANT, EN DAT WAS FOUT ZODRA ER EEN PERSPECTIEF BESTOND.
 *
 * `users.tenant_id` is de organisatie waar iemand LID van is. Sinds FD-DEMO-2
 * kan een sessie namens een andere organisatie werken, en de RLS volgt dat al:
 * élke policy staat op `collectr_actor_tenant_id()`. De browser deed dat niet.
 * Het gevolg was geen foutmelding maar iets vervelenders: de schermen filteren
 * zelf óók op tenant, dus in het advocatenperspectief vroeg de dossierlijst om
 * rijen van de THUISorganisatie terwijl de RLS alleen die van het PERSPECTIEF
 * doorliet. Snijvlak van die twee: leeg. Een lege lijst leest als "er is niets",
 * niet als "de vraag was verkeerd".
 *
 * De bron is nu dezelfde functie die de RLS gebruikt, via `rpc_mijn_effectieve_tenant`.
 * Niet de kolom nálezen en de regel naprogrammeren -- dat zou een tweede kopie
 * van dezelfde beslissing zijn, en die twee lopen uiteen.
 *
 * GEEN TENANT IS EEN ANTWOORD, GEEN STORING (18-09-2026).
 *
 * Deze hook viel bij een LEEG antwoord terug op `users.tenant_id`. Dat leek
 * streng — de thuistenant is immers altijd een organisatie waar de gebruiker bij
 * hoort — maar het maakte de platformcontext onzichtbaar: een `super_admin`
 * zonder gekozen tenant kreeg zo alsnog zijn thuistenant, en de schermen toonden
 * daar de dossiers, de werkvoorraad en de knop "Nieuw dossier" van. Gemeten op
 * productie: zes dossiers van `collectr-internal` onder de kop "Platform".
 *
 * `rpc_mijn_effectieve_tenant` geeft sinds migratie `20261324070000` bewust
 * `null` terug in die toestand. Dat antwoord wordt nu overgenomen. Alleen
 * wanneer de RPC zelf FAALT is er nog een terugval, en die draait dezelfde regel
 * op de eigen gebruikersrij — niet een tweede, mildere regel.
 */
export function useTenantContext() {
  const { user, loading: authLoading } = useAuth();
  const tenantQuery = useQuery({
    queryKey: ["auth-tenant", user?.id],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<{ tenantId: string | null; role: string | null }> => {
      const eigen = await supabase
        .from("users")
        .select("tenant_id, role, platform_effective_tenant_id")
        .eq("id", user?.id ?? "")
        .maybeSingle();
      if (eigen.error) {
        throw new Error(eigen.error.message);
      }
      const rij = (eigen.data ?? null) as {
        tenant_id: string | null;
        role: string | null;
        platform_effective_tenant_id: string | null;
      } | null;

      const effectief = await supabase.rpc("rpc_mijn_effectieve_tenant");
      if (!effectief.error) {
        /* Een leeg antwoord IS het antwoord: er is geen gekozen tenant. */
        const waarde = typeof effectief.data === "string" ? effectief.data.trim() : "";
        return { tenantId: waarde.length > 0 ? waarde : null, role: rij?.role ?? null };
      }

      console.warn("[useTenantContext] rpc_mijn_effectieve_tenant faalde", effectief.error.message);
      return {
        tenantId: effectieveTenantRegel(
          rij?.role ?? null,
          rij?.platform_effective_tenant_id ?? null,
          rij?.tenant_id ?? null,
        ),
        role: rij?.role ?? null,
      };
    },
  });

  const tenantId = tenantQuery.data?.tenantId ?? null;
  const role = tenantQuery.data?.role ?? null;

  return {
    user,
    authLoading,
    tenantId,
    tenantLoading: Boolean(user?.id) && !tenantQuery.isFetched,
    tenantError: tenantQuery.isError,
    /**
     * De platformcontext zonder gekozen tenant. Schermen tonen hierop een
     * bewuste toestand in plaats van een lege lijst of een foutmelding: leeg
     * leest als "er is niets", en dat is iets anders dan "er is niets gevraagd".
     */
    platformZonderTenant: role === "super_admin" && tenantId === null && tenantQuery.isFetched,
  };
}

export default useAuth;
